import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Table,
  Label,
} from "reactstrap";
import {
  getPurchaseReportSummary,
  getPurchaseReportDetails,
  exportPurchaseSummaryToExcel,
  exportPurchaseDetailsToExcel,
  exportPurchaseReturnSummaryToExcel,
  exportPurchaseReturnDetailsToExcel
} from "../../components/Api/GmsPurchaseOrder";
import { searchSuppliers } from "../../components/Api/GmsSupplier";
import { Filter, Download, ChevronDown, ChevronRight, BarChart3 } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin,
  getUserPermissions
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

export default function PurchaseReportPage() {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState([]);
  const [detailsData, setDetailsData] = useState({}); // { dateKey: [purchaseDetails] }
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track which rows are expanded
  const [loadingDetails, setLoadingDetails] = useState({}); // Track loading per date
  const [loading, setLoading] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10); // Fixed page size matching invoice report
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [exportingDate, setExportingDate] = useState(null);

  // Supplier Search Options
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchTimeout, setSupplierSearchTimeout] = useState(null);
  const [selectedSupplierFilterOption, setSelectedSupplierFilterOption] = useState(null);

  // Filter options
  const invoiceTypeOptions = [
    { value: 1, label: "Cash" },
    { value: 2, label: "Credit" }
  ];

  const reportTypeOptions = [
    { value: false, label: "Standard Purchase" },
    { value: true, label: "Purchase Return" }
  ];

  // Filter state
  const [filter, setFilter] = useState({
    fromDate: new Date(),
    toDate: new Date(),
    supplierId: null,
    invoiceTypeId: null,
    isReturn: false
  });

  // Check permissions
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);

  // Get active branch allowed purchase check from vertical layout sidebar config or context if needed, 
  // but page permission is sufficient as it is guarded by router & sidebar.

  // Format dates properly to avoid timezone issues
  const formatDateForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const userPermissions = await getUserPermissions();
        setPermissions(userPermissions);
        const allowed = hasPagePermissionWithAdmin(userPermissions, '/GmsPurchaseReport');
        setHasPermission(allowed);
        if (allowed) {
          // Trigger initial load inside the callback
          setTimeout(() => {
            loadSummaryDataInternal();
          }, 0);
        } else {
          Swal.fire("Access Denied", "You don't have permission to access this page.", "error").then(() => {
            navigate("/Dashboard");
          });
        }
      } catch (error) {
        console.error("Error loading permissions:", error);
      } finally {
        setPermissionsLoading(false);
      }
    };
    loadPermissions();
  }, []);

  const loadSummaryDataInternal = async (currentFilter = filter) => {
    try {
      setLoading(true);

      const formattedFromDate = formatDateForAPI(currentFilter.fromDate);
      const formattedToDate = formatDateForAPI(currentFilter.toDate);

      // Fetch active business ID from localStorage
      const userCompanyId = localStorage.getItem("userCompanyId");
      const businessId = userCompanyId ? parseInt(userCompanyId) : null;

      const filterData = {
        From_Date: formattedFromDate,
        To_Date: formattedToDate,
        PurchaseOrder_Supplier_Id: currentFilter.supplierId,
        PurchaseOrder_Business_Id: businessId,
        IsReturn: currentFilter.isReturn,
        PurchaseOrder_InvoiceType_Id: currentFilter.invoiceTypeId
      };

      console.log("📊 Loading purchase report summary with filter:", filterData);

      const response = await getPurchaseReportSummary(filterData);
      console.log("📊 Summary response:", response);

      const summaries = response?.summaries || response?.Summaries || [];
      if (summaries && Array.isArray(summaries)) {
        setSummaryData(summaries);
        setTotalCount(summaries.length);
      } else {
        setSummaryData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("❌ Error loading summary data:", error);
      Swal.fire("Error", "Failed to load purchase report summary", "error");
      setSummaryData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Wrapper for triggers
  const loadSummaryData = useCallback(() => {
    loadSummaryDataInternal(filter);
  }, [filter]);

  const loadDetailsForDate = async (date) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const dateKey = formatDateForAPI(dateObj);

    if (detailsData[dateKey]) {
      console.log("Details already loaded for", dateKey);
      return;
    }

    try {
      setLoadingDetails(prev => ({ ...prev, [dateKey]: true }));

      const formattedDate = formatDateForAPI(dateObj);
      const userCompanyId = localStorage.getItem("userCompanyId");
      const businessId = userCompanyId ? parseInt(userCompanyId) : null;

      const request = {
        From_Date: formattedDate,
        To_Date: formattedDate,
        PurchaseOrder_Supplier_Id: filter.supplierId,
        PurchaseOrder_Business_Id: businessId,
        IsReturn: filter.isReturn,
        PurchaseOrder_InvoiceType_Id: filter.invoiceTypeId,
        IncludeStockDetails: filter.isReturn // Populate stock details for returns or based on requirements
      };

      console.log("📤 Sending details request:", request);

      const response = await getPurchaseReportDetails(request);
      console.log("📥 Details response:", response);

      const details = response?.details || response?.Details || [];
      setDetailsData(prev => ({
        ...prev,
        [dateKey]: Array.isArray(details) ? details : []
      }));
    } catch (error) {
      console.error("❌ Error loading details for date:", error);
      Swal.fire("Error", `Failed to load purchase details for ${formatDate(dateObj)}`, "error");
      setDetailsData(prev => ({
        ...prev,
        [dateKey]: []
      }));
    } finally {
      setLoadingDetails(prev => ({ ...prev, [dateKey]: false }));
    }
  };

  const handleToggleRow = (date) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const dateKey = formatDateForAPI(dateObj);
    const newExpandedRows = new Set(expandedRows);

    if (newExpandedRows.has(dateKey)) {
      newExpandedRows.delete(dateKey);
    } else {
      newExpandedRows.add(dateKey);
      if (!detailsData[dateKey]) {
        loadDetailsForDate(dateObj);
      }
    }
    setExpandedRows(newExpandedRows);
  };

  const handleApplyFilter = () => {
    setPage(1);
    setExpandedRows(new Set());
    setDetailsData({});
    loadSummaryDataInternal(filter);
  };

  const handleClearFilter = () => {
    const today = new Date();
    const clearedFilter = {
      fromDate: today,
      toDate: today,
      supplierId: null,
      invoiceTypeId: null,
      isReturn: false
    };
    setFilter(clearedFilter);
    setSelectedSupplierFilterOption(null);
    setSupplierSearchOptions([]);
    setPage(1);
    setExpandedRows(new Set());
    setDetailsData({});
    loadSummaryDataInternal(clearedFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Search suppliers with debounced inputs
  const searchSuppliersManually = async (inputValue) => {
    const trimmedInput = inputValue ? inputValue.trim() : '';
    if (!trimmedInput || trimmedInput.length < 3) {
      setSupplierSearchOptions([]);
      return;
    }

    try {
      setSupplierSearchLoading(true);
      const response = await searchSuppliers(trimmedInput, 1, 20);
      if (response && response.items && Array.isArray(response.items)) {
        const options = response.items.map(s => ({
          value: s.supplier_Id,
          label: `${s.supplier_NameEn || s.supplier_Name || 'N/A'} (${s.supplier_Code || 'N/A'})`
        }));
        setSupplierSearchOptions(options);
      } else {
        setSupplierSearchOptions([]);
      }
    } catch (err) {
      console.error("Error searching suppliers:", err);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  // Export excel per date row (Details)
  const handleExportDate = async (date) => {
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      const formattedDate = formatDateForAPI(dateObj);
      setExportingDate(formattedDate);

      const userCompanyId = localStorage.getItem("userCompanyId");
      const businessId = userCompanyId ? parseInt(userCompanyId) : null;

      const request = {
        From_Date: formattedDate,
        To_Date: formattedDate,
        PurchaseOrder_Supplier_Id: filter.supplierId,
        PurchaseOrder_Business_Id: businessId,
        PurchaseOrder_InvoiceType_Id: filter.invoiceTypeId
      };

      if (filter.isReturn) {
        await exportPurchaseReturnDetailsToExcel(request);
      } else {
        await exportPurchaseDetailsToExcel(request);
      }
      Swal.fire("Success", "Excel report exported successfully", "success");
    } catch (error) {
      console.error("❌ Error exporting details:", error);
      Swal.fire("Error", "Failed to export excel report details", "error");
    } finally {
      setExportingDate(null);
    }
  };

  // Export summary report
  const handleExportSummary = async () => {
    try {
      setLoading(true);
      const formattedFromDate = formatDateForAPI(filter.fromDate);
      const formattedToDate = formatDateForAPI(filter.toDate);
      const userCompanyId = localStorage.getItem("userCompanyId");
      const businessId = userCompanyId ? parseInt(userCompanyId) : null;

      const request = {
        From_Date: formattedFromDate,
        To_Date: formattedToDate,
        PurchaseOrder_Supplier_Id: filter.supplierId,
        PurchaseOrder_Business_Id: businessId,
        PurchaseOrder_InvoiceType_Id: filter.invoiceTypeId
      };

      if (filter.isReturn) {
        await exportPurchaseReturnSummaryToExcel(request);
      } else {
        await exportPurchaseSummaryToExcel(request);
      }
      Swal.fire("Success", "Excel summary report exported successfully", "success");
    } catch (error) {
      console.error("❌ Error exporting summary:", error);
      Swal.fire("Error", "Failed to export excel summary report", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get paginated data
  const getPaginatedData = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return summaryData.slice(startIndex, endIndex);
  };

  // Calculate grand totals for footer
  const grandTotals = useMemo(() => {
    return summaryData.reduce((acc, row) => ({
      purchaseCount: acc.purchaseCount + (row.purchaseCount || row.PurchaseCount || 0),
      totalQuantity: acc.totalQuantity + (row.totalQuantity || row.TotalQuantity || 0),
      totalAmount: acc.totalAmount + (row.totalAmount || row.TotalAmount || 0),
      totalDiscount: acc.totalDiscount + (row.totalDiscount || row.TotalDiscount || 0),
      totalTax: acc.totalTax + (row.totalTax || row.TotalTax || 0),
      totalNetAmount: acc.totalNetAmount + (row.totalNetAmount || row.TotalNetAmount || 0),
    }), {
      purchaseCount: 0,
      totalQuantity: 0,
      totalAmount: 0,
      totalDiscount: 0,
      totalTax: 0,
      totalNetAmount: 0,
    });
  }, [summaryData]);

  const currencyCode = localStorage.getItem("currencyCode") || "AED";

  // Helper to format currency
  const formatCurrency = (amount) => {
    return `${currencyCode} ${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = dateString instanceof Date ? dateString : new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <ClipLoader size={50} color="#556ee6" />
            <p className="mt-3">Loading permissions...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!hasPermission) {
    return null;
  }

  const paginatedData = getPaginatedData();

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Purchase Report" breadcrumbItem="Reports" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0 d-flex align-items-center gap-2">
                    <BarChart3 size={24} style={{ color: "#556ee6" }} />
                    Purchase Report
                  </h4>
                  <div>
                    <Button
                      color="primary"
                      className="me-2 d-inline-flex align-items-center gap-1"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter size={16} />
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    <Button
                      color="success"
                      className="d-inline-flex align-items-center gap-1"
                      onClick={handleExportSummary}
                      disabled={loading || summaryData.length === 0}
                    >
                      <Download size={16} />
                      Export Summary
                    </Button>
                  </div>
                </div>

                {/* Filters */}
                {showFilters && (
                  <Card className="mb-3 border border-light shadow-sm">
                    <CardBody>
                      <Row>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_supplier">Supplier</Label>
                            <Select
                              id="filter_supplier"
                              value={selectedSupplierFilterOption}
                              onChange={(option) => {
                                setSelectedSupplierFilterOption(option);
                                setFilter({
                                  ...filter,
                                  supplierId: option ? option.value : null
                                });
                              }}
                              options={supplierSearchOptions}
                              isClearable
                              isSearchable
                              placeholder="Search Supplier Name..."
                              styles={selectStyles}
                              isLoading={supplierSearchLoading}
                              noOptionsMessage={() => "Type at least 3 characters..."}
                              loadingMessage={() => "Searching suppliers..."}
                              onInputChange={(inputValue) => {
                                if (supplierSearchTimeout) {
                                  clearTimeout(supplierSearchTimeout);
                                }
                                const timeout = setTimeout(() => {
                                  searchSuppliersManually(inputValue);
                                }, 300);
                                setSupplierSearchTimeout(timeout);
                                return inputValue;
                              }}
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_invoiceType">Invoice Type</Label>
                            <Select
                              id="filter_invoiceType"
                              value={
                                filter.invoiceTypeId
                                  ? invoiceTypeOptions.find(option => option.value === filter.invoiceTypeId)
                                  : null
                              }
                              onChange={(option) =>
                                setFilter({
                                  ...filter,
                                  invoiceTypeId: option ? option.value : null,
                                })
                              }
                              options={invoiceTypeOptions}
                              isClearable
                              placeholder="Select Invoice Type..."
                              styles={selectStyles}
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_fromDate">From Date</Label>
                            <DatePicker
                              selected={filter.fromDate}
                              onChange={(date) => setFilter({ ...filter, fromDate: date })}
                              className="form-control"
                              placeholderText="Select from date"
                              dateFormat="dd/MM/yyyy"
                              maxDate={filter.toDate}
                            />
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_toDate">To Date</Label>
                            <DatePicker
                              selected={filter.toDate}
                              onChange={(date) => setFilter({ ...filter, toDate: date })}
                              className="form-control"
                              placeholderText="Select to date"
                              dateFormat="dd/MM/yyyy"
                              minDate={filter.fromDate}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row className="align-items-center">
                        <Col md={3}>
                          <div className="mb-3">
                            <Label for="filter_reportType">Report Type</Label>
                            <Select
                              id="filter_reportType"
                              value={reportTypeOptions.find(option => option.value === filter.isReturn)}
                              onChange={(option) =>
                                setFilter({
                                  ...filter,
                                  isReturn: option ? option.value : false,
                                })
                              }
                              options={reportTypeOptions}
                              styles={selectStyles}
                            />
                          </div>
                        </Col>
                        <Col md={9} className="d-flex gap-2 justify-content-end" style={{ marginTop: "12px" }}>
                          <Button
                            color="primary"
                            onClick={handleApplyFilter}
                            disabled={loading}
                            style={{ minWidth: "110px" }}
                          >
                            Apply Filter
                          </Button>
                          <Button
                            color="secondary"
                            onClick={handleClearFilter}
                            disabled={loading}
                            style={{ minWidth: "100px" }}
                          >
                            Clear
                          </Button>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Table */}
                <Card className="shadow-none border-0">
                  <CardBody className="px-0 py-0">
                    <div className="table-responsive">
                      {loading ? (
                        <div className="text-center py-5">
                          <ClipLoader color="#556ee6" size={35} />
                          <p className="mt-2 text-muted">Loading reports...</p>
                        </div>
                      ) : (
                        <>
                          <Table bordered hover responsive className="align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '40px' }}></th>
                                <th>Date</th>
                                <th>Count</th>
                                <th>Total Quantity</th>
                                <th>Total Gross Amount</th>
                                <th>Discount</th>
                                <th>Tax</th>
                                <th>Net Amount</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedData.length > 0 ? (
                                paginatedData.map((row) => {
                                  const rowDate = row.purchaseDate || row.PurchaseDate;
                                  const dateObj = rowDate instanceof Date ? rowDate : new Date(rowDate);
                                  const dateKey = formatDateForAPI(dateObj);
                                  const isExpanded = expandedRows.has(dateKey);
                                  const dateDetails = detailsData[dateKey] || [];
                                  const isLoadingDetails = loadingDetails[dateKey];
                                  const isExporting = exportingDate === dateKey;

                                  const count = row.purchaseCount || row.PurchaseCount || 0;
                                  const totalQty = row.totalQuantity || row.TotalQuantity || 0;
                                  const totalAmt = row.totalAmount || row.TotalAmount || 0;
                                  const totalDisc = row.totalDiscount || row.TotalDiscount || 0;
                                  const totalTaxAmt = row.totalTax || row.TotalTax || 0;
                                  const totalNetAmt = row.totalNetAmount || row.TotalNetAmount || 0;

                                  return (
                                    <React.Fragment key={dateKey}>
                                      <tr>
                                        <td>
                                          <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => handleToggleRow(dateObj)}
                                            className="p-0 text-dark"
                                          >
                                            {isExpanded ? (
                                              <ChevronDown size={16} />
                                            ) : (
                                              <ChevronRight size={16} />
                                            )}
                                          </Button>
                                        </td>
                                        <td>{formatDate(dateObj)}</td>
                                        <td>{count}</td>
                                        <td>{totalQty}</td>
                                        <td>{formatCurrency(totalAmt)}</td>
                                        <td>{formatCurrency(totalDisc)}</td>
                                        <td>{formatCurrency(totalTaxAmt)}</td>
                                        <td><strong>{formatCurrency(totalNetAmt)}</strong></td>
                                        <td>
                                          <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => handleExportDate(dateObj)}
                                            disabled={isExporting || loading}
                                            className="d-inline-flex align-items-center gap-1"
                                            style={{ minWidth: '85px' }}
                                          >
                                            {isExporting ? (
                                              <ClipLoader size={12} color="#fff" />
                                            ) : (
                                              <>
                                                <Download size={14} />
                                                Export
                                              </>
                                            )}
                                          </Button>
                                        </td>
                                      </tr>
                                      {isExpanded && (
                                        <tr>
                                          <td colSpan="9" className="p-0">
                                            <div className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
                                              {isLoadingDetails ? (
                                                <div className="text-center py-3">
                                                  <ClipLoader color="#556ee6" size={20} />
                                                  <p className="mt-2 mb-0">Loading details...</p>
                                                </div>
                                              ) : dateDetails.length > 0 ? (
                                                <div>
                                                  <h6 className="mb-3 text-primary">
                                                    {filter.isReturn ? 'Purchase Return' : 'Purchase'} Details for {formatDate(dateObj)}
                                                  </h6>
                                                  <Table size="sm" bordered responsive className="mb-0 bg-white">
                                                    <thead className="table-light">
                                                      <tr>
                                                        <th>Invoice No</th>
                                                        <th>Supplier</th>
                                                        <th>Product</th>
                                                        <th>Unit</th>
                                                        <th>Quantity</th>
                                                        <th>Bonus Qty</th>
                                                        <th>Unit Price</th>
                                                        <th>Discount</th>
                                                        <th>Tax</th>
                                                        <th>Gross Amount</th>
                                                        <th>Net Amount</th>
                                                        {filter.isReturn && (
                                                          <>
                                                            <th>Batch No</th>
                                                            <th>Expiry Date</th>
                                                            <th>Cost Price</th>
                                                            <th>Sales Price</th>
                                                          </>
                                                        )}
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {dateDetails.map((detail, idx) => {
                                                        const invNo = detail.invoiceNo || detail.InvoiceNo || 'N/A';
                                                        const supp = detail.supplier || detail.Supplier || 'N/A';
                                                        const prod = detail.product || detail.Product || 'N/A';
                                                        const unitName = detail.unit || detail.Unit || 'N/A';
                                                        const qty = detail.quantity || detail.Quantity || 0;
                                                        const bonus = detail.bonusQty || detail.BonusQty || 0;
                                                        const unitPriceVal = detail.unitPrice || detail.UnitPrice || 0;
                                                        const disc = detail.discount || detail.Discount || 0;
                                                        const taxVal = detail.tax || detail.Tax || 0;
                                                        const totalVal = detail.totalAmount || detail.TotalAmount || 0;
                                                        const netVal = detail.netAmount || detail.NetAmount || 0;

                                                        return (
                                                          <tr key={idx}>
                                                            <td>{invNo}</td>
                                                            <td>{supp}</td>
                                                            <td>{prod}</td>
                                                            <td>{unitName}</td>
                                                            <td>{qty}</td>
                                                            <td>{bonus}</td>
                                                            <td>{formatCurrency(unitPriceVal)}</td>
                                                            <td>{formatCurrency(disc)}</td>
                                                            <td>{formatCurrency(taxVal)}</td>
                                                            <td>{formatCurrency(totalVal)}</td>
                                                            <td><strong>{formatCurrency(netVal)}</strong></td>
                                                            {filter.isReturn && (
                                                              <>
                                                                <td>{detail.batchNo || detail.BatchNo || 'N/A'}</td>
                                                                <td>{detail.expiryDate || detail.ExpiryDate ? formatDate(detail.expiryDate || detail.ExpiryDate) : 'N/A'}</td>
                                                                <td>{formatCurrency(detail.costPrice || detail.CostPrice)}</td>
                                                                <td>{formatCurrency(detail.salesPrice || detail.SalesPrice)}</td>
                                                              </>
                                                            )}
                                                          </tr>
                                                        );
                                                      })}
                                                    </tbody>
                                                  </Table>
                                                </div>
                                              ) : (
                                                <div className="text-center py-3">
                                                  <p className="mb-0 text-muted">No details found for this date.</p>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="9" className="text-center py-4">No records found.</td>
                                </tr>
                              )}
                            </tbody>
                            {summaryData.length > 0 && (
                              <tfoot>
                                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                  <td colSpan="2"><strong>Grand Total</strong></td>
                                  <td>{grandTotals.purchaseCount}</td>
                                  <td>{grandTotals.totalQuantity.toFixed(2)}</td>
                                  <td>{formatCurrency(grandTotals.totalAmount)}</td>
                                  <td>{formatCurrency(grandTotals.totalDiscount)}</td>
                                  <td>{formatCurrency(grandTotals.totalTax)}</td>
                                  <td><strong>{formatCurrency(grandTotals.totalNetAmount)}</strong></td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            )}
                          </Table>

                          {/* Pagination */}
                          <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                            style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                            <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                              Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                            </div>
                            {totalCount > pageSize && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Button
                                  color="light"
                                  size="sm"
                                  disabled={page === 1}
                                  onClick={() => handlePageChange(page - 1)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                    color: page === 1 ? '#6c757d' : '#495057'
                                  }}
                                >
                                  <i className="mdi mdi-chevron-left"></i>
                                </Button>

                                {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                  const pageNum = index + 1;
                                  const isActive = page === pageNum;

                                  return (
                                    <Button
                                      key={pageNum}
                                      color={isActive ? "primary" : "light"}
                                      size="sm"
                                      onClick={() => handlePageChange(pageNum)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: isActive ? '#556ee6' : '#fff',
                                        borderColor: isActive ? '#556ee6' : '#dee2e6',
                                        color: isActive ? '#fff' : '#495057',
                                        fontWeight: isActive ? '600' : '400',
                                        boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                      }}
                                    >
                                      {pageNum}
                                    </Button>
                                  );
                                })}

                                <Button
                                  color="light"
                                  size="sm"
                                  disabled={page === Math.ceil(totalCount / pageSize)}
                                  onClick={() => handlePageChange(page + 1)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                    color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                  }}
                                >
                                  <i className="mdi mdi-chevron-right"></i>
                                </Button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
